// src/components/Shared/UnitAutocomplete.tsx

import React, { useState, useEffect, useRef } from 'react'

export type Unit = {
  id:     string
  short:  string
  label:  string
}

type Props = {
  units:       Unit[]
  value:       string
  onSelect:    (sel: { label: string; unitId: string }) => void
  placeholder?: string
}

export default function UnitAutocomplete({
  units,
  value,
  onSelect,
  placeholder = 'Unit',
}: Props) {
  const [input, setInput] = useState(value || '')
  const [open, setOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<Unit[]>([])
  const containerRef = useRef<HTMLDivElement>(null)

  // 1) On focus: open & show first 10 units
  const handleFocus = () => {
    setOpen(true)
    setSuggestions(units)
  }

  // 2) Filter suggestions whenever `input` or `open` changes
  useEffect(() => {
    if (!open) return

    const q = input.trim().toLowerCase()
    if (q === '') {
      // no query? show everything
      setSuggestions(units)
    } else {
      // filter by label or short
      const hits = units
        .filter((u) =>
          u.label.toLowerCase().includes(q) ||
          u.short.toLowerCase().includes(q)
        )
        // .slice(0, 10)
      setSuggestions(hits)
    }
  }, [input, open, units])

  // 3) Close when clicking outside
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        className="w-full px-2 py-1 rounded text-sm border border-gray-300 focus:border-black focus:outline-none"
        placeholder={placeholder}
        value={input}
        onChange={(e) => {
          const v = e.target.value
          setInput(v)
          setOpen(true)
          onSelect({ label: v, unitId: '' }) // clear unitId when typing
        }}
        onFocus={handleFocus}
      />

      {open && suggestions.length > 0 && (
        <ul className="absolute z-10 w-full bg-white border rounded mt-1 max-h-32 overflow-auto">
          {suggestions.map((u, idx) => (
            <li
              key={u.id}
              className={`px-2 py-1 hover:bg-gray-100 cursor-pointer ${
                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              }`}
              onClick={() => {
                setInput(u.label)
                setOpen(false)
                onSelect({ label: u.label, unitId: u.id })
              }}
            >
              {u.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
